import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Circle, FileText, Loader2, Upload, X, AlertCircle } from 'lucide-react';
import type { WorkflowJob } from '../../graphql/types';
import { effectiveDateBounds } from '../../titleSearch/createJob';
import { SEARCH_INDEX_OPTIONS, type UseCase } from '../../titleAnalysis/inputs';
import { Pill, UseCaseSelect, type PillTone } from '../../components/ui';
import { writeWorkspaceParams } from '../urlState';
import {
  ACCEPTED_FILE_TYPES,
  EMPTY_TR_INTAKE_FORM,
  FILE_STATUS_LABELS,
  LIEN_POSITION_OPTIONS,
  SEARCH_ORDER_FIELDS,
  START_SEARCH_LABEL,
  STARTING_SEARCH_LABEL,
  SUPPORTED_FORMATS_LABEL,
  formFromJob,
  formatInvalidCount,
  formatMissingCount,
  getExistingSearchLabel,
  getExistingSearchTarget,
  validateIntake,
  validateRecordedDocumentName,
  type ExistingSearchTarget,
  type IntakeFileStatus,
  type SearchCoverageState,
  type SubjectLoanState,
  type TrIntakeForm,
} from './intakeTrModel';
import {
  UploadFailedError,
  createTitleSearch,
  loadSearchFiles,
  type CreateSearchFn,
  type CreatedSearch,
  type LoadFilesFn,
  type StoredIntakeFile,
} from './intakeTrService';

/**
 * IntakeTR: Title Search Intake Mode.
 *
 * Left: "Search order" (required) and "Subject loan" (optional) cards, built on
 * the `titleSearch/createJob.ts` validation and `effectiveDateBounds`.
 * Right: recorded documents with a per-file status, the validation summary and
 * the primary action. Creating a search uploads the documents, submits them for
 * processing and opens Review Mode, which shows a processing state until the
 * documents are done.
 *
 * For an existing search (`job` set) the form is read-only and the action reads
 * "Review extracted data" or "View findings".
 *
 * Light theme only.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7.
 */
export interface IntakeTRProps {
  /** An existing search. Omit for a new one. */
  job?: WorkflowJob | null;
  /** Reference "now" for the effective-date window; injectable for tests. */
  today?: Date;
  /** Creates the search and uploads its documents. Defaults to the real services. */
  createSearch?: CreateSearchFn;
  /** Lists the files of an existing search. Defaults to the real services. */
  loadFiles?: LoadFilesFn;
  /**
   * Called with the submitted job instead of opening Review Mode. Lets a draft
   * Workspace take over the navigation.
   */
  onCreated?: (job: WorkflowJob) => void;
  /** Called instead of changing `?mode=` for an existing search. */
  onOpenMode?: (target: ExistingSearchTarget) => void;
}

interface StagedFile {
  id: string;
  file: File;
  status: IntakeFileStatus;
  /** True once the bytes are in storage; never re-uploaded. */
  uploaded: boolean;
  error?: string;
}

const FILE_TONES: Record<IntakeFileStatus, PillTone> = {
  ready: 'neutral',
  processing: 'selected',
  processed: 'success',
  failed: 'danger',
};

const CARD = 'rounded-xl border border-gray-200 bg-white p-[18px]';
const OVERLINE = 'text-xs font-bold uppercase tracking-[0.1em] text-gray-500';
const LABEL = 'mb-1 block text-xs font-bold text-gray-700';
const ERROR_TEXT = 'mt-1 text-xs text-red-700';

function inputClass(hasError: boolean): string {
  return `h-9 w-full rounded-lg border bg-white px-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-[#834DFB] read-only:bg-gray-50 read-only:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

let stagedCounter = 0;
function nextStagedId(): string {
  stagedCounter += 1;
  return `staged-${stagedCounter}`;
}

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  wide?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, required, error, wide, children }) => (
  <div className={wide ? 'col-span-2' : undefined}>
    <label htmlFor={id} className={LABEL}>
      {label}
      {required ? (
        <>
          <span className="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </label>
    {children}
    {error ? (
      <p id={`${id}-error`} className={ERROR_TEXT}>
        {error}
      </p>
    ) : null}
  </div>
);

export const IntakeTR: React.FC<IntakeTRProps> = ({
  job = null,
  today,
  createSearch = createTitleSearch,
  loadFiles = loadSearchFiles,
  onCreated,
  onOpenMode,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const readOnly = !!job;

  const [form, setForm] = useState<TrIntakeForm>(() =>
    job ? formFromJob(job) : EMPTY_TR_INTAKE_FORM
  );
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [stored, setStored] = useState<StoredIntakeFile[]>([]);
  const [storedError, setStoredError] = useState(false);
  const [storedLoaded, setStoredLoaded] = useState(false);
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchCreated, setSearchCreated] = useState(false);
  const [dragging, setDragging] = useState(false);
  const createdRef = useRef<CreatedSearch | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep the displayed values in step with the stored search.
  useEffect(() => {
    if (job) setForm(formFromJob(job));
  }, [job]);

  // Files of an existing search; refreshed as processing progresses.
  const jobId = job?.jobId;
  const processingStatus = job?.documentProcessingStatus;
  const processedLoanCount = job?.processedLoanCount;
  useEffect(() => {
    if (!job) return undefined;
    let cancelled = false;
    loadFiles(job)
      .then((files) => {
        if (cancelled) return;
        setStored(files);
        setStoredError(false);
        setStoredLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setStoredError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, processingStatus, processedLoanCount, loadFiles]);

  const locked = readOnly || busy || searchCreated;
  const bounds = useMemo(() => effectiveDateBounds(today ?? new Date()), [today]);
  const documentCount = readOnly ? stored.length : staged.length;
  const validation = useMemo(
    () => validateIntake(form, documentCount, today),
    [form, documentCount, today]
  );
  const showErrors = attempted && !readOnly;

  const setOrder = (key: keyof TrIntakeForm['order'], value: string) =>
    setForm((prev) => ({ ...prev, order: { ...prev.order, [key]: value } }));
  const setSubject = (key: keyof SubjectLoanState, value: string) =>
    setForm((prev) => ({ ...prev, subject: { ...prev.subject, [key]: value } }));

  const orderError = (key: keyof TrIntakeForm['order']): string | undefined =>
    showErrors ? validation.orderErrors[key] : undefined;
  // `apn` sits in the Search order card but is validated with the Subject loan
  // inputs (`TitleAnalysisInputs`), so it is read from the same error map.
  const subjectError = (
    key: keyof SubjectLoanState | keyof SearchCoverageState | 'apn'
  ): string | undefined => (showErrors ? validation.analysisErrors[key] : undefined);
  const setCoverage = (patch: Partial<SearchCoverageState>) =>
    setForm((prev) => ({ ...prev, coverage: { ...prev.coverage, ...patch } }));
  const toggleIndex = (option: string) =>
    setForm((prev) => {
      const selected = prev.coverage.indexesSearched;
      return {
        ...prev,
        coverage: {
          ...prev.coverage,
          indexesSearched: selected.includes(option)
            ? selected.filter((item) => item !== option)
            : [...selected, option],
        },
      };
    });

  const addFiles = useCallback(
    (list: FileList | File[]) => {
      // Snapshot now: an input's FileList is live and is emptied when the
      // input's value is reset, so it must not be read later.
      const incoming = Array.from(list);
      const messages: string[] = [];
      const added: StagedFile[] = [];
      for (const file of incoming) {
        const problem = validateRecordedDocumentName(file.name);
        if (problem) {
          messages.push(problem);
        } else if (
          staged.some((s) => s.file.name === file.name) ||
          added.some((s) => s.file.name === file.name)
        ) {
          messages.push(`${file.name} is already in the list.`);
        } else {
          added.push({ id: nextStagedId(), file, status: 'ready', uploaded: false });
        }
      }
      if (added.length > 0) setStaged((prev) => [...prev, ...added]);
      setFileNotice(messages.length > 0 ? messages.join(' ') : null);
    },
    [staged]
  );

  const removeStaged = (id: string) => setStaged((prev) => prev.filter((s) => s.id !== id));

  const patchStaged = (id: string, patch: Partial<StagedFile>) =>
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const focusFirstInvalid = () => {
    const order = SEARCH_ORDER_FIELDS.find((f) => validation.orderErrors[f.key]);
    if (order) document.getElementById(`ts-${order.key}`)?.focus();
  };

  const openExisting = () => {
    if (!job) return;
    const target = getExistingSearchTarget(job);
    if (onOpenMode) {
      onOpenMode(target);
    } else {
      navigate({
        search: `?${writeWorkspaceParams(searchParams, { mode: target }).toString()}`,
      });
    }
  };

  const startSearch = async () => {
    setAttempted(true);
    setError(null);
    if (!validation.valid) {
      focusFirstInvalid();
      return;
    }
    setBusy(true);
    const pending = staged.filter((s) => !s.uploaded);
    try {
      const submitted = await createSearch({
        form,
        files: pending.map((s) => s.file),
        resume: createdRef.current,
        onCreated: (created) => {
          createdRef.current = created;
          setSearchCreated(true);
        },
        onFileStart: (i) => patchStaged(pending[i].id, { status: 'processing', error: undefined }),
        onFileUploaded: (i) => patchStaged(pending[i].id, { status: 'processing', uploaded: true }),
        onFileFailed: (i, message) => patchStaged(pending[i].id, { status: 'failed', error: message }),
      });
      if (onCreated) {
        onCreated(submitted);
      } else {
        navigate(
          {
            pathname: `/title-search/${submitted.jobId}`,
            search: `?${writeWorkspaceParams('', { mode: 'review' }).toString()}`,
          },
          { state: createdRef.current?.uploadBucket ? { uploadBucket: createdRef.current.uploadBucket } : undefined }
        );
      }
    } catch (err) {
      setError(
        err instanceof UploadFailedError
          ? 'Your search was created, but some documents did not upload. Remove or retry the failed files, then start again.'
          : createdRef.current
            ? 'Your search was created, but the documents could not be submitted. Try again.'
            : 'The search could not be created. Your entries are kept. Check your connection and try again.'
      );
      setBusy(false);
    }
  };

  const files: Array<{ id: string; name: string; size: number; status: IntakeFileStatus; error?: string; removable: boolean }> =
    readOnly
      ? stored.map((f) => ({ ...f, removable: false }))
      : staged.map((s) => ({
          id: s.id,
          name: s.file.name,
          size: s.file.size,
          status: s.status,
          error: s.error,
          removable: !busy && !s.uploaded,
        }));

  const orderComplete = SEARCH_ORDER_FIELDS.length - validation.missingCount;
  const orderGroupOk = validation.missingCount === 0;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(320px,380px)] items-start gap-4">
      <div className="flex min-w-0 flex-col gap-4">
        {readOnly ? (
          <p className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600">
            This search has been created. Order details are read-only.
          </p>
        ) : null}

        {/* Search order (required) */}
        <section className={CARD} aria-labelledby="ts-order-title">
          <div className="mb-3.5 flex items-baseline justify-between gap-3">
            <h2 id="ts-order-title" className="text-base font-bold text-gray-900">
              Search order
            </h2>
            <span className={OVERLINE}>Required</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            {SEARCH_ORDER_FIELDS.map((field) => {
              const id = `ts-${field.key}`;
              const err = orderError(field.key);
              const isDate = field.type === 'date';
              return (
                <Field
                  key={field.key}
                  id={id}
                  label={field.label}
                  required
                  error={err}
                  wide={field.wide}
                >
                  <input
                    id={id}
                    type={isDate && !readOnly ? 'date' : 'text'}
                    value={form.order[field.key]}
                    onChange={(e) => setOrder(field.key, e.target.value)}
                    placeholder={isDate ? undefined : field.placeholder}
                    min={isDate ? bounds.min : undefined}
                    max={isDate ? bounds.max : undefined}
                    readOnly={readOnly}
                    disabled={!readOnly && locked}
                    aria-required="true"
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `${id}-error` : undefined}
                    className={inputClass(!!err)}
                  />
                </Field>
              );
            })}
            <Field
              id="ts-apn"
              label="APN"
              error={subjectError('apn')}
            >
              <input
                id="ts-apn"
                type="text"
                value={form.apn}
                onChange={(e) => setForm((prev) => ({ ...prev, apn: e.target.value }))}
                placeholder="Optional"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('apn') ? true : undefined}
                aria-describedby={subjectError('apn') ? 'ts-apn-error' : undefined}
                className={inputClass(!!subjectError('apn'))}
              />
            </Field>
          </div>
        </section>

        {/* Subject loan (optional) */}
        <section className={CARD} aria-labelledby="ts-subject-title">
          <div className="mb-3.5 flex items-baseline justify-between gap-3">
            <h2 id="ts-subject-title" className="text-base font-bold text-gray-900">
              Subject loan
            </h2>
            <span className={OVERLINE}>Optional</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <Field id="ta-useCase" label="Use case" error={subjectError('useCase')}>
              <UseCaseSelect
                id="ta-useCase"
                value={form.subject.useCase as UseCase | ''}
                onChange={(value) => setSubject('useCase', value)}
                disabled={locked}
                disabledReason={readOnly ? 'This search has been created' : undefined}
                className="w-full"
              />
            </Field>
            <Field id="ta-vestingEntity" label="Vesting entity" error={subjectError('vestingEntity')}>
              <input
                id="ta-vestingEntity"
                type="text"
                value={form.subject.vestingEntity}
                onChange={(e) => setSubject('vestingEntity', e.target.value)}
                placeholder="Current owner of record"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('vestingEntity') ? true : undefined}
                aria-describedby={subjectError('vestingEntity') ? 'ta-vestingEntity-error' : undefined}
                className={inputClass(!!subjectError('vestingEntity'))}
              />
            </Field>
            <Field id="ta-guarantorNames" label="Guarantor names" error={subjectError('guarantorNames')}>
              <input
                id="ta-guarantorNames"
                type="text"
                value={form.subject.guarantorNames}
                onChange={(e) => setSubject('guarantorNames', e.target.value)}
                placeholder="e.g. John Smith, Jane Doe"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('guarantorNames') ? true : undefined}
                aria-describedby={subjectError('guarantorNames') ? 'ta-guarantorNames-error' : undefined}
                className={inputClass(!!subjectError('guarantorNames'))}
              />
            </Field>
            <Field id="ta-loanAmount" label="Loan amount" error={subjectError('loanAmount')}>
              <input
                id="ta-loanAmount"
                type="text"
                inputMode="decimal"
                value={form.subject.loanAmount}
                onChange={(e) => setSubject('loanAmount', e.target.value)}
                placeholder="e.g. 250000.00"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('loanAmount') ? true : undefined}
                aria-describedby={subjectError('loanAmount') ? 'ta-loanAmount-error' : undefined}
                className={inputClass(!!subjectError('loanAmount'))}
              />
            </Field>
            <Field
              id="ta-expectedLienPosition"
              label="Expected lien position"
              error={subjectError('expectedLienPosition')}
            >
              <select
                id="ta-expectedLienPosition"
                value={form.subject.expectedLienPosition}
                onChange={(e) => setSubject('expectedLienPosition', e.target.value)}
                disabled={locked}
                className={`${inputClass(!!subjectError('expectedLienPosition'))}`}
              >
                <option value="">Not specified</option>
                {LIEN_POSITION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              id="ta-currentLoanHolder"
              label="Current loan holder"
              error={subjectError('currentLoanHolder')}
            >
              <input
                id="ta-currentLoanHolder"
                type="text"
                value={form.subject.currentLoanHolder}
                onChange={(e) => setSubject('currentLoanHolder', e.target.value)}
                placeholder="Servicer or investor"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('currentLoanHolder') ? true : undefined}
                aria-describedby={subjectError('currentLoanHolder') ? 'ta-currentLoanHolder-error' : undefined}
                className={inputClass(!!subjectError('currentLoanHolder'))}
              />
            </Field>
            <Field id="ta-noteDate" label="Note date" error={subjectError('noteDate')}>
              <input
                id="ta-noteDate"
                type={readOnly ? 'text' : 'date'}
                value={form.subject.noteDate}
                onChange={(e) => setSubject('noteDate', e.target.value)}
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('noteDate') ? true : undefined}
                aria-describedby={subjectError('noteDate') ? 'ta-noteDate-error' : undefined}
                className={inputClass(!!subjectError('noteDate'))}
              />
            </Field>
            <Field
              id="ta-subjectMortgageRecordingReference"
              label="Subject mortgage recording reference"
              error={subjectError('subjectMortgageRecordingReference')}
              wide
            >
              <input
                id="ta-subjectMortgageRecordingReference"
                type="text"
                value={form.subject.subjectMortgageRecordingReference}
                onChange={(e) => setSubject('subjectMortgageRecordingReference', e.target.value)}
                placeholder="Book/page or instrument number"
                readOnly={readOnly}
                disabled={!readOnly && locked}
                aria-invalid={subjectError('subjectMortgageRecordingReference') ? true : undefined}
                aria-describedby={
                  subjectError('subjectMortgageRecordingReference')
                    ? 'ta-subjectMortgageRecordingReference-error'
                    : undefined
                }
                className={inputClass(!!subjectError('subjectMortgageRecordingReference'))}
              />
            </Field>
          </div>
        </section>

        {/* Search coverage (optional) */}
        <section className={CARD} aria-labelledby="ts-coverage-title">
          <div className="mb-3.5 flex items-baseline justify-between gap-3">
            <h2 id="ts-coverage-title" className="text-base font-bold text-gray-900">
              Search coverage
            </h2>
            <span className={OVERLINE}>Optional</span>
          </div>
          <div className="flex flex-col gap-3.5">
            <div className="max-w-[calc(50%-8px)]">
              <Field id="ta-searchStartDate" label="Search start date" error={subjectError('searchStartDate')}>
                <input
                  id="ta-searchStartDate"
                  type={readOnly ? 'text' : 'date'}
                  value={form.coverage.searchStartDate}
                  onChange={(e) => setCoverage({ searchStartDate: e.target.value })}
                  readOnly={readOnly}
                  disabled={!readOnly && locked}
                  aria-invalid={subjectError('searchStartDate') ? true : undefined}
                  aria-describedby={subjectError('searchStartDate') ? 'ta-searchStartDate-error' : undefined}
                  className={inputClass(!!subjectError('searchStartDate'))}
                />
              </Field>
            </div>
            <fieldset>
              <legend className={LABEL}>Indexes searched</legend>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {SEARCH_INDEX_OPTIONS.map((option, i) => (
                  <label key={option} htmlFor={`ta-indexesSearched-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      id={`ta-indexesSearched-${i}`}
                      type="checkbox"
                      checked={form.coverage.indexesSearched.includes(option)}
                      onChange={() => toggleIndex(option)}
                      disabled={locked}
                      className="h-4 w-4 rounded border-gray-300 accent-[#834DFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {subjectError('indexesSearched') ? (
                <p id="ta-indexesSearched-error" className={ERROR_TEXT}>
                  {subjectError('indexesSearched')}
                </p>
              ) : null}
            </fieldset>
          </div>
        </section>
      </div>

      <div className="flex min-w-0 flex-col gap-4">
        {/* Recorded documents */}
        <section className={CARD} aria-labelledby="ts-docs-title">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 id="ts-docs-title" className="text-base font-bold text-gray-900">
              Recorded documents
            </h2>
            <span className={OVERLINE}>
              {files.length} {files.length === 1 ? 'file' : 'files'}
            </span>
          </div>

          {!readOnly ? (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!locked) setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  if (!locked) addFiles(e.dataTransfer.files);
                }}
                className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center ${
                  dragging ? 'border-[#834DFB] bg-[#F5F3FF]' : 'border-gray-300 bg-gray-50'
                }`}
              >
                <Upload size={20} strokeWidth={2} className="text-[#5A21C9]" aria-hidden="true" />
                <p className="text-sm text-gray-700">Drop recorded documents here</p>
                <p className="text-xs text-gray-500">{SUPPORTED_FORMATS_LABEL}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_FILE_TYPES}
                  aria-label="Choose recorded documents"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={(e) => {
                    const picked = e.target.files ? Array.from(e.target.files) : [];
                    e.target.value = '';
                    if (picked.length > 0) addFiles(picked);
                  }}
                />
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => fileInputRef.current?.click()}
                  className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm font-bold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Choose files
                </button>
              </div>
              {fileNotice ? (
                <p role="status" className={ERROR_TEXT}>
                  {fileNotice}
                </p>
              ) : null}
            </>
          ) : null}

          {storedError && readOnly ? (
            <p role="alert" className={ERROR_TEXT}>
              The document list could not be loaded. Reload the page to try again.
            </p>
          ) : null}

          {files.length > 0 ? (
            <ul aria-label="Recorded documents" className="mt-3 divide-y divide-gray-100">
              {files.map((f) => (
                <li key={f.id} className="flex items-center gap-2.5 py-2">
                  <FileText size={16} strokeWidth={2} className="shrink-0 text-gray-500" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-gray-900" title={f.name}>
                      {f.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatSize(f.size)}
                      {f.error ? ` · ${f.error}` : ''}
                    </p>
                  </div>
                  <Pill tone={FILE_TONES[f.status]}>{FILE_STATUS_LABELS[f.status]}</Pill>
                  {f.removable ? (
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => removeStaged(f.id)}
                      className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                    >
                      <X size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : readOnly && storedLoaded && !storedError ? (
            <p className="mt-1 text-sm text-gray-500">No documents are stored for this search.</p>
          ) : null}
        </section>

        {/* Validation summary and primary action */}
        <section className={CARD} aria-labelledby="ts-summary-title">
          <h2 id="ts-summary-title" className="mb-3 text-base font-bold text-gray-900">
            {readOnly ? 'Status' : 'Before you start'}
          </h2>

          {readOnly ? (
            <>
              <p className="mb-3 text-sm text-gray-600">
                {getExistingSearchTarget(job as WorkflowJob) === 'findings'
                  ? 'The review is finalized. Findings are ready.'
                  : 'Review the extracted fields to finalize the search.'}
              </p>
              <button
                type="button"
                onClick={openExisting}
                className="h-10 w-full rounded-lg bg-[#834DFB] px-4 text-sm font-bold text-white hover:bg-[#6E2EF0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2"
              >
                {getExistingSearchLabel(job as WorkflowJob)}
              </button>
            </>
          ) : (
            <>
              {showErrors && (validation.missingCount > 0 || validation.invalidCount > 0) ? (
                <div
                  role="alert"
                  className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-[#fee2e2] px-3 py-2 text-sm text-[#991b1b]"
                >
                  <AlertCircle size={16} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <p>
                    {[
                      validation.missingCount > 0 ? formatMissingCount(validation.missingCount) : null,
                      validation.invalidCount > 0 ? formatInvalidCount(validation.invalidCount) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              ) : null}

              <ul className="mb-3 flex flex-col gap-2 text-sm">
                <li className="flex items-center gap-2">
                  {orderGroupOk ? (
                    <CheckCircle2 size={16} strokeWidth={2} className="text-[#166534]" aria-hidden="true" />
                  ) : (
                    <Circle size={16} strokeWidth={2} className="text-gray-400" aria-hidden="true" />
                  )}
                  <span className="text-gray-700">
                    Search order: {orderComplete} of {SEARCH_ORDER_FIELDS.length} required fields complete
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  {validation.hasDocuments ? (
                    <CheckCircle2 size={16} strokeWidth={2} className="text-[#166534]" aria-hidden="true" />
                  ) : (
                    <Circle size={16} strokeWidth={2} className="text-gray-400" aria-hidden="true" />
                  )}
                  <span className="text-gray-700">
                    {validation.hasDocuments
                      ? `${documentCount} recorded ${documentCount === 1 ? 'document' : 'documents'} attached`
                      : 'Add at least one recorded document'}
                  </span>
                </li>
              </ul>

              {error ? (
                <p role="alert" className="mb-3 text-sm text-red-700">
                  {error}
                </p>
              ) : null}

              <button
                type="button"
                onClick={() => void startSearch()}
                disabled={busy}
                aria-busy={busy}
                title={busy ? 'Starting the search' : undefined}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#834DFB] px-4 text-sm font-bold text-white hover:bg-[#6E2EF0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 size={16} strokeWidth={2} className="animate-spin" aria-hidden="true" /> : null}
                {busy ? STARTING_SEARCH_LABEL : START_SEARCH_LABEL}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default IntakeTR;
